import AWS from "aws-sdk";
import {
  ParticipantType as APIParticipantType,
  AppointmentRecord,
  AppointmentType,
  Language,
  PatientCase,
} from "~/api/API";
import { FileDisplayData } from "~/api/queries/useAppointmentFiles";
import {
  apiToLambdaParticipantType,
  convertDateTimeToISO,
  extractDateWithDbDefault,
  extractTimeWithDbDefault,
  inquiringPartyToParticipantType,
  noShowReasonToParticipantType,
} from "~/common/converters";
import {
  getCurrentEnv,
  getPrimaryPatientCase,
  getVisitFormLink,
  hasMedicalRecordFiles,
  isInterpreterConfirmed,
  isInterpreterExplicitlyConfirmed,
  isNoInterpreterNeeded,
} from "~/common/utils";
import {
  INDIGO_SCHEDULING_EMAIL_ADDRESS_DEV,
  INDIGO_SCHEDULING_EMAIL_ADDRESS_PROD,
  INDIGO_SCHEDULING_EMAIL_ADDRESS_STAGING,
} from "~/constants";
import { UserError } from "~/error-handling";
import {
  AppointmentType1,
  GenerateAttendanceEmailEvent,
  GenerateConfirmationEmailEvent,
  GeneratePreVisitCallSchedulingEmailEvent,
  GenerateRecordsFollowupEmailEvent,
} from "~/pydantic_models/lambdas/models/emails";
import { FEATURE_FLAGS, isFeatureEnabled } from "~/services/featureFlags";
import { formatDoctorDisplayName } from "~/utils/doctorName";
import {
  draftAttendanceEmail,
  draftConfirmationEmail,
  draftPreVisitCallSchedulingEmail,
  draftRecordsFollowUpEmail,
} from "./lambda";

export interface ValidationField {
  condition: boolean;
  name: string;
}

export interface ValidationResult {
  isValid: boolean;
  missingFields: string[];
}

export const validateAppointmentFields = (
  appointment: AppointmentRecord,
): ValidationResult => {
  const extractedAppointmentDate = extractDateWithDbDefault(appointment.date);
  const extractedAppointmentTime = extractTimeWithDbDefault(appointment.time);
  const primaryPatientCase = getPrimaryPatientCase(appointment)?.patientCase;
  const patient = primaryPatientCase?.patient;
  const appointmentLocation = appointment.location;

  const requiredFields: ValidationField[] = [
    { condition: !!appointment.type, name: "Appointment Type" },
    { condition: !!patient?.name, name: "Patient Name" },
    { condition: !!primaryPatientCase?.claimNumber, name: "Claim Number" },
    { condition: !!extractedAppointmentDate, name: "Appointment Date" },
    { condition: !!extractedAppointmentTime, name: "Appointment Time" },
    {
      condition: !!appointmentLocation?.address?.street,
      name: "Street Address",
    },
    { condition: !!appointmentLocation?.address?.city, name: "City" },
    { condition: !!appointmentLocation?.address?.state, name: "State" },
    { condition: !!appointmentLocation?.address?.zip, name: "ZIP Code" },
    {
      condition: !!(
        appointment.doctor && formatDoctorDisplayName(appointment.doctor)
      ),
      name: "Doctor Name",
    },
    {
      condition: !!appointment.doctor?.officePhone,
      name: "Doctor Office Phone",
    },
  ];

  const missingFields = requiredFields
    .filter((field) => !field.condition)
    .map((field) => field.name);

  return {
    isValid: missingFields.length === 0,
    missingFields,
  };
};

const SUPPORTED_APPOINTMENT_TYPES = [
  AppointmentType.QME,
  AppointmentType.QME_REEVALUATION,
  AppointmentType.AME,
  AppointmentType.AME_REEVALUATION,
  AppointmentType.SIBTF,
  AppointmentType.SIBTF_REEVALUATION,
  AppointmentType.IME,
  AppointmentType.IME_REEVALUATION,
  AppointmentType.IME_ADR,
  AppointmentType.IME_ADR_REEVALUATION,
] as const;

type SupportedAPIType = (typeof SUPPORTED_APPOINTMENT_TYPES)[number];

const INDIGO_SCHEDULING_EMAIL_ADDRESS = (() => {
  switch (getCurrentEnv()) {
    case "dev":
      return INDIGO_SCHEDULING_EMAIL_ADDRESS_DEV;
    case "stage":
      return INDIGO_SCHEDULING_EMAIL_ADDRESS_STAGING;
    case "prod":
      return INDIGO_SCHEDULING_EMAIL_ADDRESS_PROD;
  }
})();

export const isSupportedAppointmentType = (
  value: AppointmentType | null | undefined,
): value is SupportedAPIType => {
  return (
    !!value && SUPPORTED_APPOINTMENT_TYPES.includes(value as SupportedAPIType)
  );
};

/**
 * Get party-specific CC emails based on the recipient party.
 * Each party type has its own list of CC emails that should be included
 * when sending emails to that party.
 */
const getPartyCcEmails = (
  patientCase: PatientCase | null | undefined,
  recipientParty: APIParticipantType,
): string[] => {
  if (!patientCase) return [];

  let partyCcEmails: Array<string | null> | null | undefined;

  switch (recipientParty) {
    case APIParticipantType.ADJUSTER:
      partyCcEmails = patientCase.adjusterCcEmails;
      break;
    case APIParticipantType.APPLICANT_ATTORNEY:
      partyCcEmails = patientCase.applicantAttorneyCcEmails;
      break;
    case APIParticipantType.DEFENSE_ATTORNEY:
      partyCcEmails = patientCase.defenseAttorneyCcEmails;
      break;
    default:
      // PATIENT doesn't have CC emails
      return [];
  }

  // Filter out null values and return valid emails
  return (partyCcEmails ?? []).filter((email): email is string => !!email);
};

function mapAppointmentTypeToPydantic(
  type: SupportedAPIType,
): AppointmentType1 {
  switch (type) {
    case AppointmentType.QME:
      return "QME";
    case AppointmentType.QME_REEVALUATION:
      return "QME_REEVALUATION";
    case AppointmentType.AME:
      return "AME";
    case AppointmentType.AME_REEVALUATION:
      return "AME_REEVALUATION";
    case AppointmentType.SIBTF:
      return "SIBTF";
    case AppointmentType.SIBTF_REEVALUATION:
      return "SIBTF_REEVALUATION";
    case AppointmentType.IME:
      return "IME";
    case AppointmentType.IME_REEVALUATION:
      return "IME_REEVALUATION";
    case AppointmentType.IME_ADR:
      return "IME_ADR";
    case AppointmentType.IME_ADR_REEVALUATION:
      return "IME_ADR_REEVALUATION";
  }
}

export const draftConfirmationEmailForAppointment = async (
  lambda: AWS.Lambda,
  appointment: AppointmentRecord,
  recipient_email: string,
  recipient_party: APIParticipantType,
) => {
  const validationResult = validateAppointmentFields(appointment);
  if (!validationResult.isValid) {
    throw new UserError(
      `Missing required fields: ${validationResult.missingFields.join(", ")}`,
    );
  }

  const appointmentType = appointment.type;

  if (!isSupportedAppointmentType(appointmentType)) {
    throw new UserError(
      `Unsupported appointment type: ${appointmentType ?? "null"}`,
    );
  }

  const extractedAppointmentDate = extractDateWithDbDefault(appointment.date);
  const extractedAppointmentTime = extractTimeWithDbDefault(appointment.time);
  const primaryPatientCase = getPrimaryPatientCase(appointment)?.patientCase;
  const patient = primaryPatientCase?.patient;
  const appointmentLocation = appointment.location;

  // Collect CC emails: ccPerson email + party-specific CC emails
  const ccEmails: string[] = [];
  if (primaryPatientCase?.ccPerson?.contact?.email) {
    ccEmails.push(primaryPatientCase.ccPerson.contact.email);
  }
  ccEmails.push(...getPartyCcEmails(primaryPatientCase, recipient_party));

  const data: GenerateConfirmationEmailEvent = {
    email_type: "CONFIRMATION",
    appointment_type: mapAppointmentTypeToPydantic(appointmentType),
    sender_email: INDIGO_SCHEDULING_EMAIL_ADDRESS,
    patient_name: patient?.name ?? "",
    claim_number: primaryPatientCase?.claimNumber ?? "",
    appointment_time: convertDateTimeToISO(
      extractedAppointmentDate!,
      extractedAppointmentTime!,
    ),
    location_address: {
      street: appointmentLocation!.address!.street!,
      city: appointmentLocation!.address!.city!,
      state: appointmentLocation!.address!.state!,
      zip_code: appointmentLocation!.address!.zip!,
    },
    doctor_name: formatDoctorDisplayName(appointment.doctor!),
    doctor_number: {
      phone: appointment.doctor!.officePhone!,
    },
    medical_assistant_name: appointment.schedulingAssistant?.name ?? null,
    scheduling_party: appointment.inquirerInfo?.inquiringParty
      ? inquiringPartyToParticipantType(appointment.inquirerInfo.inquiringParty)
      : null,
    recipient_email,
    recipient_party: apiToLambdaParticipantType(recipient_party),
    cc_emails: ccEmails,
    interpreter_needed: !isNoInterpreterNeeded(
      patient?.language || Language.ENGLISH,
    ),
    parking_fee: appointmentLocation?.parkingRate ?? null,
    front_door_locked: appointmentLocation?.isFrontDoorLocked ?? null,
  };

  await draftConfirmationEmail(lambda, data);
};

export const draftRecordsFollowUpEmailForAppointment = async (
  lambda: AWS.Lambda,
  appointment: AppointmentRecord,
  recipient_email: string,
  recipient_party: APIParticipantType,
  files: FileDisplayData[],
) => {
  const validationResult = validateAppointmentFields(appointment);
  if (!validationResult.isValid) {
    throw new UserError(
      `Missing required fields: ${validationResult.missingFields.join(", ")}`,
    );
  }

  if (!isSupportedAppointmentType(appointment.type)) {
    throw new UserError(
      `Unsupported appointment type: ${appointment.type ?? "null"}`,
    );
  }

  const extractedAppointmentDate = extractDateWithDbDefault(appointment.date);
  const extractedAppointmentTime = extractTimeWithDbDefault(appointment.time);
  const primaryPatientCase = getPrimaryPatientCase(appointment)?.patientCase;
  const patient = primaryPatientCase?.patient;
  const appointmentLocation = appointment.location;

  const interpreterNeeded = !isNoInterpreterNeeded(
    patient?.language || Language.ENGLISH,
  );

  // Collect CC emails: ccPerson email + party-specific CC emails
  const ccEmails: string[] = [];
  if (primaryPatientCase?.ccPerson?.contact?.email) {
    ccEmails.push(primaryPatientCase.ccPerson.contact.email);
  }
  ccEmails.push(...getPartyCcEmails(primaryPatientCase, recipient_party));

  // Under INTERPRETER_CONFIRMATION use the explicit confirmed flag; otherwise
  // fall back to the legacy "any interpreter field filled" heuristic. Without
  // user context this resolves to legacy until the flag is GA'd (globally on).
  const interpreterConfirmationEnabled = await isFeatureEnabled(
    FEATURE_FLAGS.INTERPRETER_CONFIRMATION,
  );
  const interpreterConfirmed = interpreterConfirmationEnabled
    ? isInterpreterExplicitlyConfirmed(appointment.interpreter)
    : isInterpreterConfirmed(appointment.interpreter);

  const data: GenerateRecordsFollowupEmailEvent = {
    email_type: "RECORDS_FOLLOWUP",
    appointment_type: appointment.type,
    sender_email: INDIGO_SCHEDULING_EMAIL_ADDRESS,
    patient_name: patient?.name ?? "",
    claim_number: primaryPatientCase?.claimNumber ?? "",
    appointment_time: convertDateTimeToISO(
      extractedAppointmentDate!,
      extractedAppointmentTime!,
    ),
    location_address: {
      street: appointmentLocation!.address!.street!,
      city: appointmentLocation!.address!.city!,
      state: appointmentLocation!.address!.state!,
      zip_code: appointmentLocation!.address!.zip!,
    },
    doctor_name: formatDoctorDisplayName(appointment.doctor!),
    doctor_number: {
      phone: appointment.doctor!.officePhone!,
    },
    medical_assistant_name: appointment.medicalAssistant?.name ?? null,
    scheduling_party: appointment.inquirerInfo?.inquiringParty
      ? inquiringPartyToParticipantType(appointment.inquirerInfo.inquiringParty)
      : null,
    recipient_email,
    recipient_party: apiToLambdaParticipantType(recipient_party),
    cc_emails: ccEmails,
    interpreter_needed: interpreterNeeded,
    interpreter_confirmed: interpreterConfirmed,
    records_received: hasMedicalRecordFiles(files, true),
  };

  await draftRecordsFollowUpEmail(lambda, data);
};

export const draftAttendanceEmailForAppointment = async (
  lambda: AWS.Lambda,
  appointment: AppointmentRecord,
  recipient_email: string,
  recipient_party: APIParticipantType,
) => {
  const validationResult = validateAppointmentFields(appointment);
  if (!validationResult.isValid) {
    throw new UserError(
      `Missing required fields: ${validationResult.missingFields.join(", ")}`,
    );
  }

  if (!isSupportedAppointmentType(appointment.type)) {
    throw new UserError(
      `Unsupported appointment type: ${appointment.type ?? "null"}`,
    );
  }

  const extractedAppointmentDate = extractDateWithDbDefault(appointment.date);
  const extractedAppointmentTime = extractTimeWithDbDefault(appointment.time);
  const primaryPatientCase = getPrimaryPatientCase(appointment)?.patientCase;
  const patient = primaryPatientCase?.patient;
  const appointmentLocation = appointment.location;

  // Collect CC emails: ccPerson email + party-specific CC emails
  const ccEmails: string[] = [];
  if (primaryPatientCase?.ccPerson?.contact?.email) {
    ccEmails.push(primaryPatientCase.ccPerson.contact.email);
  }
  ccEmails.push(...getPartyCcEmails(primaryPatientCase, recipient_party));

  const data: GenerateAttendanceEmailEvent = {
    email_type: "ATTENDANCE",
    appointment_type: appointment.type,
    sender_email: INDIGO_SCHEDULING_EMAIL_ADDRESS,
    patient_name: patient?.name ?? "",
    claim_number: primaryPatientCase?.claimNumber ?? "",
    appointment_time: convertDateTimeToISO(
      extractedAppointmentDate!,
      extractedAppointmentTime!,
    ),
    location_address: {
      street: appointmentLocation!.address!.street!,
      city: appointmentLocation!.address!.city!,
      state: appointmentLocation!.address!.state!,
      zip_code: appointmentLocation!.address!.zip!,
    },
    doctor_name: formatDoctorDisplayName(appointment.doctor!),
    doctor_number: {
      phone: appointment.doctor!.officePhone!,
    },
    medical_assistant_name: appointment.medicalAssistant?.name ?? null,
    scheduling_party: appointment.inquirerInfo?.inquiringParty
      ? inquiringPartyToParticipantType(appointment.inquirerInfo.inquiringParty)
      : null,
    recipient_email,
    recipient_party: apiToLambdaParticipantType(recipient_party),
    cc_emails: ccEmails,
    no_show: !!appointment.noShowReason,
    no_show_party: appointment.noShowReason
      ? noShowReasonToParticipantType(appointment.noShowReason)
      : null,
  };

  await draftAttendanceEmail(lambda, data);
};

export const draftPreVisitCallSchedulingEmailForAppointment = async (
  lambda: AWS.Lambda,
  appointment: AppointmentRecord,
  recipient_email: string | null, // allow null as the draft is sometimes used for texting
  recipient_party: APIParticipantType,
) => {
  const validationResult = validateAppointmentFields(appointment);
  if (!validationResult.isValid) {
    throw new UserError(
      `Missing required fields: ${validationResult.missingFields.join(", ")}`,
    );
  }

  if (!isSupportedAppointmentType(appointment.type)) {
    throw new UserError(
      `Unsupported appointment type: ${appointment.type ?? "null"}`,
    );
  }

  const extractedAppointmentDate = extractDateWithDbDefault(appointment.date);
  const extractedAppointmentTime = extractTimeWithDbDefault(appointment.time);
  const primaryPatientCase = getPrimaryPatientCase(appointment)?.patientCase;
  const patient = primaryPatientCase?.patient;
  const appointmentLocation = appointment.location;

  const data: GeneratePreVisitCallSchedulingEmailEvent = {
    email_type: "PRE_VISIT_CALL_SCHEDULING",
    sender_email: INDIGO_SCHEDULING_EMAIL_ADDRESS,
    appointment_type: appointment.type,
    patient_name: patient?.name ?? "",
    claim_number: primaryPatientCase?.claimNumber ?? "",
    appointment_time: convertDateTimeToISO(
      extractedAppointmentDate!,
      extractedAppointmentTime!,
    ),
    location_address: {
      street: appointmentLocation!.address!.street!,
      city: appointmentLocation!.address!.city!,
      state: appointmentLocation!.address!.state!,
      zip_code: appointmentLocation!.address!.zip!,
    },
    doctor_name: formatDoctorDisplayName(appointment.doctor!),
    doctor_number: {
      phone: appointment.doctor!.officePhone!,
    },
    medical_assistant_name: appointment.medicalAssistant?.name ?? null,
    scheduling_party: appointment.inquirerInfo?.inquiringParty
      ? inquiringPartyToParticipantType(appointment.inquirerInfo.inquiringParty)
      : null,
    recipient_email,
    recipient_party: apiToLambdaParticipantType(recipient_party),
    cc_emails: [], // Skipping cc emails for now to avoid accidentally cc'ing different parties
    patient_phone: patient?.contact?.phone
      ? {
          phone: patient.contact.phone,
        }
      : null,
    schedule_link:
      appointment.medicalAssistant?.preVisitCallScheduleLink ?? null,
    visit_form_link:
      patient?.language === Language.SPANISH
        ? getVisitFormLink(appointment.id, "es")
        : getVisitFormLink(appointment.id),
  };

  await draftPreVisitCallSchedulingEmail(lambda, data);
};
